import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { Response } from '../../../../_shared/models';
import { PaginatedResult } from '../../../../_shared/models/paginated-result.model';
import {
  AssignComplianceAssetsRequest,
  AssignedAssetComplianceHistory,
  AssignedAssetComplianceHistoryRequest,
  AssignedAssets,
  ChecklistRequirementResults,
  ChecklistRequirementResultsRequest,
  ComplianceArchiveRequest,
  Compliance,
  ComplianceAssignedAsset,
  ComplianceChecklist,
  ComplianceFrequency,
  ComplianceStandard,
  CreateCustomComplianceRequest,
  DeleteAssignedAssetRequest,
  EditAssignedAssetFrequencyRequest,
  GetAssignedAssetsRequest,
  GetChecklistsRequest,
  GetCompliancesRequest,
  AddComplianceChecklistRequirementsRequest,
  UpdateChecklistRequirementDetailsRequest,
  RoutineType,
  UploadComplianceExcelRequest
} from '../models/compliances.model';

@Injectable({
  providedIn: 'root'
})
export class CompliancesLibraryService {

  private httpClient = inject(HttpClient);

  getCompliancesAsync(request: GetCompliancesRequest, cancellation: Subject<void>): Promise<Response<PaginatedResult<Compliance>>> {
    return this.getPaginatedAsync('compliances', request, cancellation);
  }

  getArchivedCompliancesAsync(request: GetCompliancesRequest, cancellation: Subject<void>): Promise<Response<PaginatedResult<Compliance>>> {
    return this.getPaginatedAsync('paginated-archived', request, cancellation);
  }

  private getPaginatedAsync(path: string, request: GetCompliancesRequest, cancellation: Subject<void>): Promise<Response<PaginatedResult<Compliance>>> {
    return firstValueFrom(
      this.httpClient.post<Response<PaginatedResult<Compliance>>>(
        `${environment.apiEndpoint}/compliance-library/${path}`,
        request
      ).pipe(takeUntil(cancellation))
    );
  }

  getComplianceStandardsAsync(accountIds: number[], cancellation: Subject<void>): Promise<Response<ComplianceStandard[]>> {
    return firstValueFrom(
      this.httpClient.post<Response<ComplianceStandard[]>>(
        `${environment.apiEndpoint}/compliance-library/compliance-standards`,
        { accountIds }
      ).pipe(takeUntil(cancellation))
    );
  }

  assignAssetsAsync(request: AssignComplianceAssetsRequest): Promise<void> {
    return firstValueFrom(
      this.httpClient.post<void>(
        `${environment.apiEndpoint}/compliance-library/assign-assets`,
        request
      )
    );
  }

  archiveComplianceAsync(request: ComplianceArchiveRequest): Promise<void> {
    return firstValueFrom(
      this.httpClient.put<void>(
        `${environment.apiEndpoint}/compliance-library/archive`,
        request
      )
    );
  }

  restoreComplianceAsync(request: ComplianceArchiveRequest): Promise<void> {
    return firstValueFrom(
      this.httpClient.put<void>(
        `${environment.apiEndpoint}/compliance-library/restore`,
        request
      )
    );
  }

  getComplianceAccountIdsAsync(complianceId: number, cancellation: Subject<void>): Promise<Response<number[]>> {
    return firstValueFrom(
      this.httpClient.get<Response<number[]>>(
        `${environment.apiEndpoint}/compliance-library/account-ids`,
        { params: new HttpParams().set('complianceId', complianceId) }
      ).pipe(takeUntil(cancellation))
    );
  }

  getAssignedAssetsAsync(request: GetAssignedAssetsRequest, cancellation: Subject<void>): Promise<Response<PaginatedResult<AssignedAssets>>> {
    return firstValueFrom(
      this.httpClient.get<Response<PaginatedResult<AssignedAssets>>>(
        `${environment.apiEndpoint}/compliance-library/assigned-assets`,
        { params: this.toHttpParams(request) }
      ).pipe(takeUntil(cancellation))
    );
  }

  getComplianceAssignedAssetsAsync(complianceId: number, cancellation: Subject<void>): Promise<Response<ComplianceAssignedAsset[]>> {
    return firstValueFrom(
      this.httpClient.get(
        `${environment.apiEndpoint}/compliance-library/assigned-assets/${complianceId}/assets`
      ).pipe(takeUntil(cancellation))
    );
  }

  deleteAssignedAssetAsync(request: DeleteAssignedAssetRequest): Promise<void> {
    return firstValueFrom(
      this.httpClient.delete<void>(
        `${environment.apiEndpoint}/compliance-library/assigned-asset`,
        { body: request }
      )
    );
  }

  editAssignedAssetFrequencyAsync(request: EditAssignedAssetFrequencyRequest): Promise<void> {
    return firstValueFrom(
      this.httpClient.put<void>(
        `${environment.apiEndpoint}/compliance-library/assigned-asset/frequency`,
        request
      )
    );
  }

  getAssignedAssetComplianceHistoryAsync(request: AssignedAssetComplianceHistoryRequest, cancellation: Subject<void>): Promise<Response<PaginatedResult<AssignedAssetComplianceHistory>>> {
    return firstValueFrom(
      this.httpClient.post(
        `${environment.apiEndpoint}/compliance-library/assigned-asset/history/filter`,
        request
      ).pipe(takeUntil(cancellation))
    );
  }

  getChecklistRequirementResultsAsync(request: ChecklistRequirementResultsRequest, cancellation: Subject<void>): Promise<Response<ChecklistRequirementResults[]>> {
    return firstValueFrom(
      this.httpClient.post<Response<ChecklistRequirementResults[]>>(
        `${environment.apiEndpoint}/compliance-library/assigned-asset/checklist-requirement-results/filter`,
        request
      ).pipe(takeUntil(cancellation))
    );
  }

  getComplianceFrequenciesAsync(cancellation: Subject<void>): Promise<Response<ComplianceFrequency[]>> {
    return firstValueFrom(
      this.httpClient.get<Response<ComplianceFrequency[]>>(
        `${environment.apiEndpoint}/compliance-library/frequencies`
      ).pipe(takeUntil(cancellation))
    );
  }

  getRoutineTypesAsync(cancellation: Subject<void>): Promise<Response<RoutineType[]>> {
    return firstValueFrom(
      this.httpClient.get<Response<RoutineType[]>>(
        `${environment.apiEndpoint}/compliance-library/routine-types`
      ).pipe(takeUntil(cancellation))
    );
  }

  getRoutineTypesByAccountAsync(accountId: number, cancellation: Subject<void>): Promise<Response<RoutineType[]>> {
    return firstValueFrom(
      this.httpClient.get<Response<RoutineType[]>>(
        `${environment.apiEndpoint}/compliance-library/routine-types/${accountId}`
      ).pipe(takeUntil(cancellation))
    );
  }

  createCustomComplianceAsync(request: CreateCustomComplianceRequest): Promise<void> {
    return firstValueFrom(
      this.httpClient.post<void>(
        `${environment.apiEndpoint}/compliance-library/custom`,
        request
      )
    );
  }

  bulkUploadCompliancesAsync(request: UploadComplianceExcelRequest, cancellation: Subject<void>): Promise<Response<string[]>> {
    return firstValueFrom(
      this.httpClient.post<Response<string[]>>(
        `${environment.apiEndpoint}/compliance-library/bulk-upload-compliances`,
        request
      ).pipe(takeUntil(cancellation))
    );
  }

  archiveChecklistRequirementAsync(requirementId: number, complianceId: number): Promise<void> {
    return firstValueFrom(
      this.httpClient.put<void>(
        `${environment.apiEndpoint}/compliance-library/archive-requirement`,
        null,
        { params: new HttpParams().set('requirementId', requirementId).set('complianceId', complianceId) }
      )
    );
  }

  restoreChecklistRequirementAsync(requirementId: number, complianceId: number): Promise<void> {
    return firstValueFrom(
      this.httpClient.put<void>(
        `${environment.apiEndpoint}/compliance-library/restore-requirement`,
        null,
        { params: new HttpParams().set('requirementId', requirementId).set('complianceId', complianceId) }
      )
    );
  }

  toggleRequirementDocUploadAsync(requirementId: number, complianceId: number, allowsDocUpload: boolean, cancellation: Subject<void>): Promise<void> {
    return firstValueFrom(
      this.httpClient.put<void>(
        `${environment.apiEndpoint}/compliance-library/toggle-requirement-doc-upload`,
        null,
        { params: new HttpParams().set('requirementId', requirementId).set('complianceId', complianceId).set('allowDocumentUpload', allowsDocUpload) }
      ).pipe(takeUntil(cancellation))
    );
  }

  toggleRequirementEnabledAsync(requirementId: number, complianceId: number, assetId: number, isEnabled: boolean, cancellation: Subject<void>): Promise<void> {
    return firstValueFrom(
      this.httpClient.put<void>(
        `${environment.apiEndpoint}/compliance-library/toggle-requirement-enabled`,
        null,
        {
          params: new HttpParams()
            .set('requirementId', requirementId)
            .set('complianceId', complianceId)
            .set('assetId', assetId)
            .set('isEnabled', isEnabled)
        }
      ).pipe(takeUntil(cancellation))
    );
  }

  getChecklistsAsync(request: GetChecklistsRequest, cancellation: Subject<void>): Promise<Response<ComplianceChecklist[]>> {
    return firstValueFrom(
      this.httpClient.get<Response<ComplianceChecklist[]>>(
        `${environment.apiEndpoint}/compliance-library/checklists`,
        { params: this.toHttpParams(request) }
      ).pipe(takeUntil(cancellation))
    );
  }

  getChecklistsIncludingDisabledAsync(request: GetChecklistsRequest, cancellation: Subject<void>): Promise<Response<ComplianceChecklist[]>> {
    return firstValueFrom(
      this.httpClient.get<Response<ComplianceChecklist[]>>(
        `${environment.apiEndpoint}/compliance-library/checklists/including-disabled`,
        { params: this.toHttpParams(request) }
      ).pipe(takeUntil(cancellation))
    );
  }

  addComplianceChecklistRequirementsAsync(request: AddComplianceChecklistRequirementsRequest): Promise<void> {
    return firstValueFrom(
      this.httpClient.post<void>(
        `${environment.apiEndpoint}/compliance-library/checklist-requirements`,
        request
      )
    );
  }

  updateChecklistRequirementDetailsAsync(request: UpdateChecklistRequirementDetailsRequest): Promise<void> {
    return firstValueFrom(
      this.httpClient.put<void>(
        `${environment.apiEndpoint}/compliance-library/requirement-details`,
        request
      )
    );
  }

  deleteChecklistRequirementAsync(requirementId: number, complianceId: number): Promise<void> {
    return firstValueFrom(
      this.httpClient.delete<void>(
        `${environment.apiEndpoint}/compliance-library/requirement`,
        { body: { requirementId, complianceId } }
      )
    );
  }

  private toHttpParams(obj: object): HttpParams {
    return Object.entries(obj).reduce(
      (params, [key, value]) => value != null ? params.set(key, String(value)) : params,
      new HttpParams()
    );
  }
}